import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { PromoteStatus } from '@internal/backstage-plugin-sql-pipeline-common';

import { sqlPipelineApiRef } from '../api/SqlPipelineApi';

/**
 * 운영계 반영 버튼.
 *
 * dp-lakehouse 는 main = 개발계, prod = 운영계다. 반영은 main 타깃 PR 에
 * `/promote` 코멘트를 남기면 레포의 promote.yml 이 그 PR 이 바꾼 파일만
 * prod 로 승격한다. 따라서 승격 단위는 "이 DAG" 가 아니라 **그 PR** 이고,
 * 다이얼로그가 그 범위를 보여준다. (DPD-13508)
 *
 * `packages/app/src/components/pipeline/DeployProdButton.tsx` 에서 옮겼다.
 * 변경점: dagPath 를 클라이언트가 계산해 보내던 것을 제거했다 — 서버가
 * 엔티티에서 파생한다(신뢰 경계).
 */
export const PromoteToProdButton = () => {
  const { entity } = useEntity();
  const api = useApi(sqlPipelineApiRef);
  const alertApi = useApi(alertApiRef);
  const pipelineName = entity.metadata.name;

  const [status, setStatus] = useState<PromoteStatus | null>(null);
  const [checking, setChecking] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      setStatus(await api.getPromoteStatus(pipelineName));
    } catch {
      setStatus(null);
    } finally {
      setChecking(false);
    }
  }, [api, pipelineName]);

  useEffect(() => {
    check();
  }, [check]);

  const handlePromote = async () => {
    setPromoting(true);
    setDialogOpen(false);
    try {
      const result = await api.requestPromote(pipelineName);
      alertApi.post({
        message: result.alreadyRequested
          ? `PR #${result.prNumber} 에 이미 운영계 반영이 요청되어 있습니다. 승격 PR 머지 후 반영됩니다. 문의: #help_data`
          : `PR #${result.prNumber} 에 운영계 반영을 요청했습니다. 승격 PR 이 생성되며 Review 후 반영됩니다. 문의: #help_data`,
        severity: 'success',
      });
      await check();
    } catch (e: any) {
      alertApi.post({
        message: e?.message ?? '요청에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setPromoting(false);
    }
  };

  if (checking) {
    return (
      <Button variant="contained" disabled startIcon={<CircularProgress size={16} />}>
        확인 중...
      </Button>
    );
  }

  const hasDiff = Boolean(status?.hasDiff);
  const pr = status?.candidatePr ?? null;
  const canPromote = hasDiff && pr !== null && !promoting;

  let tooltip = `개발계(${status?.devBranch})의 변경사항을 운영계(${status?.prodBranch})에 반영 요청합니다`;
  if (!hasDiff) {
    tooltip = `개발계(${status?.devBranch})와 운영계(${status?.prodBranch})가 동일합니다`;
  } else if (!pr) {
    tooltip = '이 DAG 를 변경한 PR 을 찾을 수 없어 운영계 반영을 요청할 수 없습니다';
  }

  const dagFiles = status?.changedFiles.map(f => f.filename) ?? [];
  const otherFiles = (status?.promoteScope ?? []).filter(
    f => !dagFiles.includes(f),
  );

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              promoting ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />
            }
            onClick={() => setDialogOpen(true)}
            disabled={!canPromote}
            style={canPromote ? { backgroundColor: '#e65100', color: '#fff' } : undefined}
          >
            운영계 반영
          </Button>
        </span>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>운영계 반영 확인</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{pipelineName}</strong> 의 개발계(<code>{status?.devBranch}</code>)
            변경사항을 운영계(<code>{status?.prodBranch}</code>)에 반영 요청합니다.
          </Typography>

          {pr && (
            <Box mt={2}>
              <Typography variant="body2">
                대상 PR:{' '}
                <Link href={pr.url} target="_blank" rel="noopener">
                  #{pr.number} {pr.title}
                </Link>
              </Typography>
            </Box>
          )}

          {otherFiles.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="error" component="div">
                주의: 승격은 PR 단위로 동작하므로 이 DAG 외에 아래 파일도 함께
                운영계로 반영됩니다.
                <ul style={{ marginTop: 4, marginBottom: 0 }}>
                  {otherFiles.slice(0, 10).map(f => (
                    <li key={f}>
                      <code>{f}</code>
                    </li>
                  ))}
                  {otherFiles.length > 10 && (
                    <li>… 외 {otherFiles.length - 10}건</li>
                  )}
                </ul>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button onClick={handlePromote} color="primary" variant="contained">
            반영 요청
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
